import { EyeSlashIcon } from '@heroicons/react/24/solid';
import {
  Position,
  NodeProps,
  NodeResizer,
  NodeToolbar,
  useReactFlow,
  OnResizeStart,
} from '@xyflow/react';
import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import { LandscapeToPortraitIcon } from 'src/components/icons/LandscapeToPortraitIcon';
import { LockIcon } from 'src/components/icons/LockIcon';
import { LockOpenIcon } from 'src/components/icons/LockOpenIcon';
import { PortraitToLandscapeIcon } from 'src/components/icons/PortraitToLandscapeIcon';
import { nodeZIndex } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo';
import { FrameOrientation } from 'src/types';
import { twMerge } from 'tailwind-merge';

import { EDataType } from '../../models/node-data';
import './project-frame-node.scss';
import ProjectFrameOverlay from './project-frame-overlay';

function ProjectFrameNode({
  id,
  data,
  selected,
  width,
  height,
  dragging,
  positionAbsoluteX,
  positionAbsoluteY,
}: NodeProps) {
  const { exportSettings, setExportSettings } = useGlobalContext();
  const { takeSnapshot } = useUndoRedo();
  const { setNodes, getNodes } = useReactFlow();

  const nodeDataOrientation =
    (data.frameOrientation as FrameOrientation) || 'portrait';
  const isVisible = exportSettings?.isVisible !== false; // Use exportSettings as source of truth
  const isLocked = data.isLocked === true; // Default to false

  // Calculate min dimensions based on orientation to maintain aspect ratio
  const minDimensions = useMemo(() => {
    if (nodeDataOrientation === 'landscape') {
      // Landscape: 1123/794 = 1.414
      return { minWidth: 400, minHeight: Math.round(400 / 1.414) };
    }
    // Portrait: 794/1123 = 0.707
    return { minWidth: 400, minHeight: Math.round(400 / 0.707) };
  }, [nodeDataOrientation]);

  const onResizeStart: OnResizeStart = useCallback(
    (event, node) => {
      takeSnapshot();
    },
    [takeSnapshot],
  );

  const toggleOrientation = useCallback(() => {
    takeSnapshot();
    const newOrientation =
      nodeDataOrientation === 'portrait' ? 'landscape' : 'portrait';

    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              width: node.height,
              height: node.width,
              data: {
                ...node.data,
                frameOrientation: newOrientation,
              },
            }
          : node,
      ),
    );
  }, [nodeDataOrientation, id, setNodes, takeSnapshot]);

  const toggleVisibility = useCallback(() => {
    takeSnapshot();
    const newVisibility = !isVisible;

    // Update exportSettings first (source of truth)
    setExportSettings({
      ...(exportSettings || {}),
      isVisible: newVisibility,
    });

    // Then handle node: remove if hiding, ensure exists if showing
    const allNodes = getNodes();
    const existingFrameNode = allNodes.find(
      (node) => node.type === EDataType.ProjectFrame,
    );

    if (!newVisibility && existingFrameNode) {
      // Hide: Remove node from nodes array (same as menu logic)
      setNodes((nds) => nds.filter((node) => node.id !== existingFrameNode.id));
    }
    // If showing and node doesn't exist, menu logic will handle creating it
  }, [
    isVisible,
    exportSettings,
    setExportSettings,
    getNodes,
    setNodes,
    takeSnapshot,
  ]);

  const toggleLock = useCallback(() => {
    takeSnapshot();
    const nextLocked = !isLocked;
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              selectable: !nextLocked,
              draggable: !nextLocked,
              selected: nextLocked ? false : node.selected,
              data: {
                ...node.data,
                isLocked: nextLocked,
              },
              zIndex: nodeZIndex(EDataType.ProjectFrame),
            }
          : node,
      ),
    );
    toast.success(nextLocked ? 'Frame locked' : 'Frame unlocked');
  }, [id, isLocked, setNodes, takeSnapshot]);

  // When locked, ensure node cannot be selected or dragged (e.g. after load from saved state)
  useEffect(() => {
    if (!isLocked) return;
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id &&
        (node.selectable !== false || node.draggable !== false)
          ? {
              ...node,
              selectable: false,
              draggable: false,
              selected: false,
            }
          : node,
      ),
    );
  }, [id, isLocked, setNodes]);

  // Sync node dimensions/orientation/visibility to export settings (primitives only to avoid render loop)
  useEffect(() => {
    setExportSettings({
      frameOrientation: nodeDataOrientation,
      width: width || 0,
      height: height || 0,
      positionX: positionAbsoluteX || 0,
      positionY: positionAbsoluteY || 0,
      isVisible,
    });
  }, [
    nodeDataOrientation,
    width,
    height,
    positionAbsoluteX,
    positionAbsoluteY,
    isVisible,
    setExportSettings,
  ]);

  const renderZIndex = useMemo(() => {
    if (selected) {
      return 1000;
    }
    if (isLocked) {
      return 1;
    }
    return nodeZIndex(EDataType.ProjectFrame);
  }, [selected, isLocked]);

  const showCursorTip = selected || isLocked;
  const cursorTipText = isLocked
    ? 'Frame is locked'
    : 'Click outside the frame to deselect it';

  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const frameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showCursorTip) {
      setCursorPos(null);
      return () => {};
    }
    const handleMove = (e: MouseEvent) => {
      const el = frameRef.current;
      if (!el) {
        setCursorPos(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (inside) {
        setCursorPos({ x: e.clientX, y: e.clientY });
      } else {
        setCursorPos(null);
      }
    };
    document.addEventListener('mousemove', handleMove);
    return () => document.removeEventListener('mousemove', handleMove);
  }, [showCursorTip]);

  const CURSOR_TIP_OFFSET = 12;
  const cursorTooltip =
    showCursorTip && cursorPos
      ? createPortal(
          <div
            className="project-frame-node-cursor-tip"
            style={{
              left: cursorPos.x + CURSOR_TIP_OFFSET,
              top: cursorPos.y + CURSOR_TIP_OFFSET,
            }}
            role="tooltip"
          >
            {cursorTipText}
          </div>,
          document.body,
        )
      : null;

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {cursorTooltip}
      <NodeResizer
        minWidth={minDimensions.minWidth}
        minHeight={minDimensions.minHeight}
        keepAspectRatio
        isVisible={selected && !isLocked}
        onResizeStart={onResizeStart}
        data-testid="project-frame-node-resizer"
      />
      <div
        ref={frameRef}
        className={twMerge(
          'project-frame-node',
          selected && 'project-frame-node--selected',
          isLocked && 'project-frame-node--locked',
        )}
        style={{ width, height, zIndex: renderZIndex }}
        data-locked={isLocked}
        onContextMenu={(e) => {
          if (isLocked) {
            e.preventDefault();
            e.stopPropagation();
            toggleLock();
          }
        }}
      >
        <NodeToolbar
          className="nodrag"
          position={Position.Bottom}
          isVisible={selected || isLocked}
        >
          <div className="flex w-full items-center justify-center gap-2">
            <Button
              data-testid="project-frame-toggle-orientation"
              data-tooltip-id="app-tooltip"
              data-tooltip-content={`Switch to ${nodeDataOrientation === 'portrait' ? 'Landscape' : 'Portrait'}`}
              color="blue"
              onClick={toggleOrientation}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {nodeDataOrientation === 'portrait' ? (
                <PortraitToLandscapeIcon className="h-4 w-4" />
              ) : (
                <LandscapeToPortraitIcon className="h-4 w-4" />
              )}
            </Button>
            <Button
              data-testid="project-frame-toggle-visibility"
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Hide Frame"
              color="green"
              onClick={toggleVisibility}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <EyeSlashIcon className="h-4 w-4" />
            </Button>
            <Button
              data-testid="project-frame-toggle-lock"
              data-tooltip-id="app-tooltip"
              data-tooltip-content={isLocked ? 'Unlock Frame' : 'Lock Frame'}
              color="red"
              onClick={toggleLock}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {isLocked ? (
                <LockIcon className="h-4 w-4" />
              ) : (
                <LockOpenIcon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </NodeToolbar>
        <div
          className={twMerge(
            'transition-all duration-200 ease-in-out',
            dragging ? 'opacity-50' : 'opacity-100',
          )}
        >
          <ProjectFrameOverlay
            wrapWidth={width || 0}
            orientation={nodeDataOrientation}
          />
        </div>
      </div>
    </>
  );
}

export default memo(ProjectFrameNode);
